import { Component, OnInit } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { ModalConfig } from 'src/app/core/models/modalConfig';
import { AuthService } from 'src/app/core/services/auth.service';
import { OutreachService } from 'src/app/core/services/outreach.service';
import { CandidateService } from 'src/app/core/services/candidate.service';
import { JobService } from 'src/app/core/services/job.service';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';

// Native port of the legacy userNotificationsActionModal (shared/user-notifications/
// user-notifications-actions-modal.html + the 4 inline $uibModal controller variants
// previously opened from user-notifications.component.ts's viewAllNotifications(),
// goToWorkflow(), openResultsModal() and openModal()). All 8 actionType branches of the
// original template/controller are preserved here as one component, matching how the
// legacy modal was a single shared template driven purely by `actionType` + resolves.
@Component({
  selector: 'app-user-notifications-actions-modal',
  templateUrl: './user-notifications-actions-modal.component.html',
  styleUrls: ['./user-notifications-actions-modal.component.css']
})
export class UserNotificationsActionsModalComponent implements OnInit {

  public onClose: Subject<any>;

  modalData: any = {};

  actionType: string;
  actionLabel: string;

  // reviewJobPull
  jobId: any;
  jbiTransactionId: any;
  processedHTMLText: any;
  candidateDtoMap: any;
  notificationId: any;

  // viewAllNotifications
  context: any;
  searchObject: any;
  numberOfNewNotifications: any = 0;
  userNotificationControls: any = {};

  // shared across several action types
  jobTypeList: any;
  salaryDurationOptions: any;

  // candidateBooklet / emailNotification
  documentDtoMap: any;

  // bulkUploadDetails
  bulkUploadJob: any;
  bulkUploadDate: string;

  // resumeProcessedCandidates
  candidatesCount: any;
  headingTitle: any;

  // candidateNoLongerMatch / outreachNoResponse
  notification: any;
  isStaffingCompany: any;
  isOutreachExpiryDataExists: any;
  candidateId: any;
  selectAllJobMatches: any = false;
  allJobMatchesDisabled: any;
  isSendOutreachAgainEnabled: any;
  sendOutreachAgain: any = false;

  // closeCallback passed through from whichever opener needs one (goToWorkflow's
  // reviewJobPull, and openResultsModal's outreachNoResponse/candidateNoLongerMatch)
  private closeCallback: (() => void) | null = null;

  callbackObject: any = { callbackType: 'cancel', entity: {} };

  userId: any;
  companyId: any;

  constructor(
    private bsModalRef: BsModalRef,
    public bsModalService: BsModalService,
    private authService: AuthService,
    private outreachService: OutreachService,
    private candidateService: CandidateService,
    private jobService: JobService,
    private alertsAndNotificationsService: AlertAndNotificationsService
  ) {
    this.onClose = new Subject();
    if (this.bsModalService.config.initialState) {
      this.modalData = this.bsModalService.config.initialState;
    }
  }

  get bulkUploadFailedFiles(): any[] {
    const map = this.candidateDtoMap;
    if (map && Array.isArray(map.failedFileList)) {
      return map.failedFileList;
    }
    if (map && Array.isArray(map.failedFiles)) {
      return map.failedFiles;
    }
    const unprocessed = map && Array.isArray(map.unProcessedCandidates) ? map.unProcessedCandidates : [];
    return unprocessed.map(c => ({ fileName: c.failedFileName, reason: c.failureReason }));
  }

  get bulkUploadCandidates(): any[] {
    const map = this.candidateDtoMap;
    return map && Array.isArray(map.candidates) ? map.candidates : [];
  }

  get bulkUploadJobId(): string {
    const map = this.candidateDtoMap;
    return this.jobId || (map && (map.jobId || (this.bulkUploadCandidates.find(c => c.jobId) || {}).jobId));
  }

  get bulkUploadHeading(): string {
    if (!this.bulkUploadJob) { return ''; }
    const reqNumber = this.bulkUploadJob.requisitionNumber || this.bulkUploadJob.reqNumber;
    const total = (this.candidateDtoMap && this.candidateDtoMap.totalResumes) || 0;
    const date = this.bulkUploadDate ? ` at <span class='emerald'>'${this.bulkUploadDate}'</span>` : '';
    return `Bulk resume upload for <span class='emerald'>'${total}'</span> resumes for `
      + `<span class='emerald'>'${this.bulkUploadJob.title}'</span>`
      + (reqNumber ? ` (<span class='emerald'>'${reqNumber}'</span>)` : '') + date;
  }

  markOutreachSent(): void {
    if (this.notification) {
      this.notification.outreachSent = true;
    }
  }

  private loadBulkUploadJob(): void {
    const jobId = this.bulkUploadJobId;
    if (!jobId) { return; }
    this.jobService.getJob(jobId, (job) => {
      this.bulkUploadJob = job;
    }, () => { });
  }

  ngOnInit(): void {
    this.actionType = this.modalData.actionType;
    this.actionLabel = this.modalData.actionLabel;

    this.jobId = this.modalData.jobId;
    this.jbiTransactionId = this.modalData.jbiTransactionId;
    this.candidateDtoMap = this.modalData.candidateDtoMap;
    this.bulkUploadDate = this.modalData.bulkUploadDate;
    if (this.actionType === 'bulkUploadDetails') {
      this.loadBulkUploadJob();
    }
    this.notificationId = this.modalData.notificationId;
    this.processedHTMLText = this.modalData.notificationHTMLText;
    if (this.actionType === 'reviewJobPull' && this.processedHTMLText) {
      this.processedHTMLText = this.processedHTMLText.replace(/Finished processing/g, 'Processed');
    }

    this.context = this.modalData.context;
    this.numberOfNewNotifications = 0;
    this.userNotificationControls = {};
    this.userNotificationControls.setNewNotificationsAsViewed = null;
    this.searchObject = this.modalData.searchObject;

    this.jobTypeList = this.modalData.jobTypeList;
    this.salaryDurationOptions = this.modalData.salaryDurationOptions;

    this.documentDtoMap = this.modalData.documentDtoMap;

    this.candidatesCount = this.modalData.candidatesCount;
    this.headingTitle = this.modalData.headingTitle;

    this.notification = this.modalData.notification;
    this.isStaffingCompany = this.modalData.isStaffingCompany;
    this.isOutreachExpiryDataExists = this.modalData.isOutreachExpiryDataExists;
    this.candidateId = this.modalData.candidateId;
    this.selectAllJobMatches = this.modalData.selectAllJobMatches || false;
    this.allJobMatchesDisabled = this.modalData.allJobMatchesDisabled;
    this.isSendOutreachAgainEnabled = this.modalData.isSendOutreachAgainEnabled;

    this.closeCallback = this.modalData.closeCallback || null;

    this.userId = this.authService.getUserDetails().id;
    this.companyId = this.authService.getUserDetails().company.companyId;
  }

  closeModal(): void {
    this.callbackObject.callbackType = 'close';
    this.onClose.next(this.callbackObject);
    this.bsModalRef.hide();
    if (this.closeCallback) {
      this.closeCallback();
    }
  }

  // reviewJobPull: <review-candidate-pull-from-job-boards>'s close-callback output -
  // matches the legacy closeCallbackModal(), which (unlike closeModal()) never invoked
  // the resolved closeCallback.
  closeCallbackModal(): void {
    this.callbackObject.callbackType = 'close';
    this.onClose.next(this.callbackObject);
    this.bsModalRef.hide();
  }

  checkIfAllDisabled(jobMatches): boolean {
    if (!jobMatches || jobMatches.length === 0) {
      return true;
    }
    return jobMatches.every(jm => jm.outreachDisabled === true);
  }

  openModalCallback(jobMatch): void {
    this._openNestedModal(jobMatch, 'jobProfile', this.candidateId);
  }

  openJobCompareModalCallback(jobMatch): void {
    this._openNestedModal(jobMatch, 'jobCompare', this.candidateId);
  }

  // Mirrors the legacy openModal(jobMatch, actionType, candidateId): the outreachNoResponse
  // view opens a second instance of this same modal for the 'jobProfile'/'jobCompare' actionType.
  private _openNestedModal(jobMatch, actionType, candidateId): void {
    const actionLabel = actionType === 'jobProfile' ? 'Job Profile' : 'Candidate Job Comparison';
    const config = new ModalConfig();
    config.class = 'custom-modal-xl';
    config.ignoreBackdropClick = true;
    config.keyboard = false;
    config.initialState = {
      actionType: actionType,
      actionLabel: actionLabel,
      notification: jobMatch,
      candidateId: candidateId
    };
    this.bsModalService.show(UserNotificationsActionsModalComponent, config as ModalOptions);
  }

  onJobMatchSelectionChange(jobMatches): void {
    if (jobMatches && jobMatches.length > 0) {
      const enabled = jobMatches.filter(jm => !jm.outreachDisabled);
      this.selectAllJobMatches = enabled.length > 0 &&
        enabled.every(jm => jm.jobMatchSelected === true);
    } else {
      this.selectAllJobMatches = false;
    }
  }

  toggleSelectAllJobMatches(jobMatches): void {
    if (jobMatches && jobMatches.length > 0) {
      jobMatches.forEach(jm => {
        jm.jobMatchSelected = !jm.outreachDisabled && this.selectAllJobMatches;
      });
    }
  }

  sendCandidateOutreachAgain(jobMatches): void {
    this.sendSelectedJobMatchOutreachEmail(jobMatches, this.candidateId, this.notification.candidateType, this.notification.clientOrBuId, () => {
      this.closeModal();
    });
  }

  sendSelectedJobMatchOutreachEmail(jobMatches, candidateId, candidateType, clientOrBuId, successCallback): void {
    const selectedJobMatches = jobMatches.filter(jobMatch => (jobMatch.jobMatchSelected && !jobMatch.outreachDisabled));
    if (selectedJobMatches.length === 0) {
      this.alertsAndNotificationsService.showAlert('No Selection', 'Please select at least One Job Match to Send Outreach to Candidate.', 'warning');
      return;
    } else {
      let outreachObject: any = {};
      let candidateIds = [
        {
          id: candidateId,
          candidateType: candidateType ? candidateType : 'OWN_CANDIDATES',
          source: 'CANDIDATE_CARD',
          jobIds: jobMatches.filter(jobMatch => (jobMatch.jobMatchSelected && !jobMatch.outreachDisabled)).map(jobMatch => jobMatch.jobId),
          jobMatchId: ''
        }
      ];
      outreachObject = {
        candidates: candidateIds,
        userId: this.userId,
        companyId: this.companyId,
        clientOrBuId: clientOrBuId ? clientOrBuId : jobMatches[0].clientOrBuId ? jobMatches[0].clientOrBuId : ''
      };

      this.candidateService.sendOutreachEmail(outreachObject, (data) => {
        this.alertsAndNotificationsService.showBannerMessage(data.message, 'success');
        if (successCallback) {
          successCallback();
        }
      }, (error) => {
        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
      });
    }
  }

}
